import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { mapErrors, safeString } from "../../Utils";
import { ResourceLoading } from "../ResourceLoading";
import { PMField } from "../PMField";
import PMListboxFormik from "../PMListboxFormik";
import { PMDialog } from "../PMDialog";
import CustomerDialog from "../../dashboard/customers/dialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const FETCH_URL = "/customers/";

export const CustomerSelector = ({ showField = true, ...props }) => {
  const {
    setFieldValue,
    values,
    onChange,
    errors,
    module,
    module_param,
    fields,
    searchQuery: initialSearchQuery,
  } = props;

  const name = props?.name ?? "customer_id";
  const label = props?.label ?? "Customer";

  const { t } = useTranslation();

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [inCreateView, setInCreateView] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  // ── Search & list state ───────────────────────────────────────────────────
  const [query, setQuery] = useState(initialSearchQuery || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open && !inCreateView);

  // ── Selected state ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState({
    id: null,
    field1: "Ingen valgt",
    field2: null,
  });

  // ── Sync selected from parent ─────────────────────────────────────────────
  useEffect(() => {
    if (props?.selected?.id) {
      setSelected({
        id: props.selected.id,
        field1: safeString(
          props.selected?.displayTitle ?? props.selected?.name,
          "Ingen valgt"
        ),
        syncStatus: props.selected?.syncStatus,
      });
    } else if (!props?.selected?.id) {
      setSelected({ id: null, field1: "Ingen valgt", field2: null });
    }
  }, [props.selected]);

  // ── Open/close helpers ────────────────────────────────────────────────────
  const handleOpen = () => {
    if (props.disabled) return;
    if (initialSearchQuery) setQuery(initialSearchQuery);
    setOpen(true);
    setInCreateView(false);
  };

  const handleClose = () => {
    setOpen(false);
    setInCreateView(false);
    setQuery("");
  };

  // ── Propagate selection ───────────────────────────────────────────────────
  const applySelection = (id, item) => {
    if (values) setFieldValue(name, id);
    if (onChange)
      onChange({ target: { name, value: id, data: item } });
  };

  const handleSelect = (item) => {
    if (item.id !== null) {
      setSelected({
        id: item.id,
        field1: safeString(item.primary, "Ingen valgt"),
      });
      applySelection(item.id, item.object ?? item);
    } else {
      setSelected({ id: null, field1: "Ingen valgt", field2: null });
      applySelection(null, null);
    }
    handleClose();
  };

  const handleSelectFromForm = (data) => {
    setSelected({
      id: data?.id,
      field1: safeString(data?.displayTitle ?? data?.name, "Ingen valgt"),
    });
    applySelection(data?.id, data);
    handleClose();
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchItems = useCallback(async (searchQuery) => {
    setLoading(true);
    try {
      const response = await axios({
        method: "get",
        url: FETCH_URL,
        params: {
          query: searchQuery || undefined,
          page: 1,
          sortBy: "updated_at",
          sort: "asc",
        },
      });

      const array = [
        { id: null, value: null, primary: "Ingen valgt", secondary: null },
      ];
      if (response.data?.data?.length > 0) {
        response.data.data.forEach((row) => {
          array.push({
            id: row.id,
            value: row.id,
            primary: safeString(row?.displayTitle ?? row?.name, "Ingen valgt"),
            secondary:
              [row?.address, row?.postal, row?.place]
                .filter(Boolean)
                .join(" ") || null,
            object: row,
          });
        });
      }
      setItems(array);
    } catch {
      setItems([{ id: null, value: null, primary: "Ingen valgt", secondary: null }]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useCallback(
    debounce((q) => fetchItems(q), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, debouncedFetch]);

  // ── Create form ───────────────────────────────────────────────────────────
  const customerTypes = [
    { name: "customer_type", value: "", label: "Ikke valgt" },
    { name: "customer_type", value: "private", label: "Customer" },
    { name: "customer_type", value: "company", label: "Firmakunde" },
    { name: "customer_type", value: "supplier", label: "Leverandør" },
    {
      name: "customer_type",
      value: "customer_company_supplier",
      label: "Firmakunde / Leverandør",
    },
  ];

  const createOrUpdate = (vals) => {
    const data = {
      customer: { ...vals },
      module,
      module_param,
    };
    if (vals?.id) {
      return axios.put(`${FETCH_URL}${vals.id}`, data).then((r) => r);
    }
    return axios.post(FETCH_URL, data).then((r) => r);
  };

  const renderCreateForm = () => (
    <Formik
      initialValues={{
        id: null,
        name: fields?.name ?? null,
        email: fields?.email ?? null,
        mobile: fields?.mobile ?? null,
        customer_type: "",
        vat: "",
        postal_address: {
          address: fields?.address ?? null,
          address2: fields?.address2 ?? null,
          postal: fields?.postal ?? null,
          place: fields?.place ?? null,
        },
        submit: null,
      }}
      validationSchema={Yup.object({
        name: Yup.string().required("Name is required"),
        mobile: Yup.string().required("Mobile is required"),
      })}
      onSubmit={(vals, helpers) => {
        try {
          toast.promise(createOrUpdate(vals), {
            loading: t("Saving...."),
            success: (data) => {
              if (data.status === 500 || data.status === 400)
                throw new Error("Server error");
              if (data.data.errors) throw new Error(mapErrors(data.data.errors));
              fetchItems(query);
              if (data?.data?.id) handleSelectFromForm(data?.data);
              return t("Data was saved!");
            },
            error: () => t("Something went wrong. Could not save data!"),
          });
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        } catch (err) {
          toast.error("Something went wrong...");
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form className="space-y-4 p-4">
          <PMListboxFormik
            label={t("customer.type")}
            name="customer_type"
            options={customerTypes}
            {...formik}
          />
          <PMField name="name" type="text" label={t("Name")} {...formik} />
          {["company", "supplier", "customer_company_supplier"].includes(
            formik.values.customer_type
          ) && (
            <PMField
              name="vat"
              type="text"
              label={t("Org. Nummer")}
              {...formik}
            />
          )}
          <PMField name="email" type="text" label={t("Email")} {...formik} />
          <PMField name="mobile" type="text" label={t("Mobile")} {...formik} />

          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide pt-1">
            Postadresse
          </p>
          <PMField
            name="postal_address.address"
            type="text"
            label={t("Address")}
            {...formik}
          />
          <PMField
            name="postal_address.address2"
            type="text"
            label={t("Address 2")}
            {...formik}
          />
          <PMField
            name="postal_address.postal"
            type="text"
            label={t("Postal")}
            {...formik}
          />
          <PMField
            name="postal_address.place"
            type="text"
            label={t("Place")}
            {...formik}
          />

          <div className="pt-2">
            <button
              type="button"
              onClick={formik.submitForm}
              disabled={formik.isSubmitting}
              className="w-full inline-flex justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {t("create")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  // ── Trigger button ────────────────────────────────────────────────────────
  const hasSelection = selected?.id != null;

  const triggerButton = showField ? (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {props?.labelAction?.action ? (
          <button
            type="button"
            onClick={props.labelAction.action}
            className="text-blue-500 hover:text-blue-700"
          >
            {t(label)}
          </button>
        ) : (
          <span
            className={
              hasSelection ? "text-blue-500 hover:text-blue-700 cursor-pointer" : ""
            }
            onClick={hasSelection ? () => setCustomerDialogOpen(true) : undefined}
          >
            {t(label)}
          </span>
        )}
      </label>
      <button
        type="button"
        disabled={props.disabled}
        onClick={handleOpen}
        className={[
          "relative w-full flex items-center justify-between",
          "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
          "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
          props.disabled
            ? "cursor-not-allowed border-gray-200 text-gray-400"
            : hasSelection
            ? "border-blue-400 text-gray-900 hover:border-blue-500"
            : "border-gray-300 text-gray-500 hover:border-gray-400",
        ].join(" ")}
      >
        <span className="truncate">{selected.field1}</span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
      </button>
      {errors?.[name] && (
        <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
      )}
    </div>
  ) : null;

  // ── Dialog content ────────────────────────────────────────────────────────
  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          {inCreateView ? (
            <button
              type="button"
              onClick={() => setInCreateView(false)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Tilbake
            </button>
          ) : (
            <h3 className="text-base font-semibold text-gray-900">
              {t(label)}
            </h3>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!inCreateView && (
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søk etter kunde..."
              autoFocus
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {inCreateView ? (
          renderCreateForm()
        ) : loading ? (
          <div className="flex items-center justify-center py-10">
            <ResourceLoading />
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => {
              const isSelected = item.id === selected?.id;
              return (
                <li
                  key={item.id ?? "none"}
                  onClick={() => handleSelect(item)}
                  className={[
                    "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                    isSelected
                      ? "bg-blue-50 text-blue-700"
                      : "hover:bg-gray-50 text-gray-900",
                  ].join(" ")}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.primary}</p>
                    {item.secondary && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {item.secondary}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      {!inCreateView && (
        <div className="flex-shrink-0 border-t border-gray-200 p-3">
          <button
            type="button"
            onClick={() => setInCreateView(true)}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Opprett ny kunde
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {triggerButton}

      <PMDialog
        open={open}
        onClose={handleClose}
        size="md"
        dense
        zIndex={3000}
        closeOnOutsideClick
      >
        {dialogContent}
      </PMDialog>

      <CustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        readOnly={true}
        selected={{ id: selected?.id }}
      />
    </>
  );
};
